import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty } from "lodash";
import { Form } from "react-bootstrap";
import { FiEdit3 } from "react-icons/fi";

import {
  linkEmployerToCrm,
  unlinkEmployerToCrm,
  getEmployerCrmLink,
  syncCrm,
} from "actions/adminActions";
import { adminConstants } from "actions/types";
import { clearErrors } from "actions/errorActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import IconSpinner from "components/IconSpinner";
import Alert from "components/Alert";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import SimpleModal from "components/SimpleModal";
import Button from "components/Button";
import type { RootState } from "types/state";

type CrmLinkEntity = RootState["employer"]["crmLink"];

const errorSelector = createErrorSelector([
  adminConstants.GET_COMPANY_CRM_LINK,
  adminConstants.LINK_COMPANY_TO_CRM,
  adminConstants.UNLINK_COMPANY_TO_CRM,
  adminConstants.SYNC_CRM_DATA,
]);

const loadingSelector = createLoadingSelector(adminConstants.SYNC_CRM_DATA);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    crmLink: state.employer.crmLink,
  };
};

const mapDispatchToProps = {
  clearErrors,
  getEmployerCrmLink,
  linkEmployerToCrm,
  unlinkEmployerToCrm,
  syncCrm,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  isUpdating: boolean;
  showEditModal: boolean;
  currentCrmLink: CrmLinkEntity;
}

class CrmLink extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      isUpdating: false,

      showEditModal: false,
      currentCrmLink: {
        ...props.crmLink,
      },
    };
  }

  async componentDidMount() {
    // these keys are global, so a failure on another employer would show here
    this.props.clearErrors([
      adminConstants.LINK_COMPANY_TO_CRM,
      adminConstants.UNLINK_COMPANY_TO_CRM,
      adminConstants.SYNC_CRM_DATA,
    ]);
    void this.props
      .getEmployerCrmLink(this.props.client as ApolloClient<object>, {
        // companyId is structurally optional on OwnProps but always supplied; the
        // GraphQL variable is required — preserve the original pass-through.
        companyId: this.props.companyId as string,
      })
      .then(() => {
        this.setState({
          initialFetching: false,
        });
      });
  }

  _getTableMetadata = (): Array<IconTableColumn<CrmLinkEntity>> => {
    return [
      {
        key: "providerReferenceId",
        label: "HubSpot Company Id",
      },
      {
        label: "Action",
        customComponent: (props) => {
          return (
            // found-not-fixed (CrmLink.tsx _getTableMetadata customComponent):
            // `key` is React-reserved and stripped from spread props before they
            // reach this component, and crmLink has no `key` field anyway — so
            // props.key is always undefined at runtime. Preserved verbatim.
            <span
              key={props.key as React.Key | null | undefined}
              className="action-icon"
              onClick={() => {
                this.setState({
                  showEditModal: true,
                  currentCrmLink: props,
                });
              }}
            >
              <FiEdit3 size={18} stroke="#96AAC4" fill="white" />
            </span>
          );
        },
      },
    ];
  };

  saveHubSpotId = () => {
    this.setState({ isUpdating: true });
    void this.props
      .linkEmployerToCrm(this.props.client as ApolloClient<object>, {
        companyId: this.props.companyId as string,
        providerReferenceId: this.state.currentCrmLink
          .providerReferenceId as string,
      })
      .then(() => {
        return this.props.getEmployerCrmLink(
          this.props.client as ApolloClient<object>,
          {
            companyId: this.props.companyId as string,
          }
        );
      })
      .then(() => {
        this.setState({ showEditModal: false, isUpdating: false });
      });
  };

  render() {
    if (this.state.initialFetching) return <IconSpinner centered />;
    return (
      <div>
        <SimpleModal
          show={this.state.showEditModal}
          onClose={() => {
            this.setState({ showEditModal: false });
          }}
          onSubmit={this.saveHubSpotId}
          title="Update HubSpot Company Id"
          buttonText="Update"
          closeButtonText="Cancel"
          btnDisabled={isEmpty(this.state.currentCrmLink.providerReferenceId)}
          isFetching={this.state.isUpdating}
          isXhrRequest
          showCloseButton
        >
          <Form.Control
            autoFocus
            value={this.state.currentCrmLink.providerReferenceId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              this.setState({
                currentCrmLink: {
                  ...this.state.currentCrmLink,
                  providerReferenceId: event.target.value,
                },
              });
            }}
          />
        </SimpleModal>
        <IconTableHeader tableHeader="CRM Links" />
        <IconTable
          columns={this._getTableMetadata()}
          data={[this.props.crmLink]}
        />
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <Button
          btnLabel={"Sync"}
          name="submit"
          disabled={isEmpty(this.props.crmLink.providerReferenceId)}
          withArrow={true}
          loading={this.props.isFetching}
          onClick={() =>
            this.props.syncCrm(this.props.client as ApolloClient<object>, {
              companyId: this.props.companyId as string,
            })
          }
        />
      </div>
    );
  }
}

export default connector(withApollo<Props>(CrmLink));
